import React, { createContext, useContext, useState, useEffect } from 'react';
import loadGoogleMaps from '../Utility/loadGoogleMaps';

interface GoogleMapsContextType {
    isLoaded: boolean;
    loadError: Error | null;
    setMap: (map: google.maps.Map | null) => void;
    isMarkerLibraryLoaded: boolean;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
    isLoaded: false,
    loadError: null,
    setMap: () => { },
    isMarkerLibraryLoaded: false,
});

export const useGoogleMaps = () => {
    const context = useContext(GoogleMapsContext);
    if (!context) {
        throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
    }
    return context;
};

interface GoogleMapsProviderProps {
    children: React.ReactNode;
}

export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({ children }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState<Error | null>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [isMarkerLibraryLoaded, setIsMarkerLibraryLoaded] = useState(false);

    useEffect(() => {
        // Check if Google Maps is already loaded
        if (window.google && window.google.maps) {
            setIsLoaded(true);
            setLoadError(null);
            // Check if marker library is available
            if (window.google.maps.marker) {
                setIsMarkerLibraryLoaded(true);
            }
            return;
        }

        // Load Google Maps API
        try {
            loadGoogleMaps(() => {
                // Only mark as loaded when the global is truly available
                if (window.google && window.google.maps) {
                    console.log('✅ Google Maps API loaded successfully');
                    setIsLoaded(true);
                    setLoadError(null);

                    // Check if marker library is available
                    if (window.google.maps.marker) {
                        console.log('✅ Google Maps Marker library loaded successfully');
                        setIsMarkerLibraryLoaded(true);
                    } else {
                        console.warn('⚠️ Google Maps Marker library not available');
                        setIsMarkerLibraryLoaded(false);
                    }
                } else {
                    console.error('❌ Google Maps API callback fired but window.google.maps is unavailable');
                    setIsLoaded(false);
                    setLoadError(new Error('Google Maps failed to load. Check API key/config.'));
                }
            });
        } catch (error) {
            console.error('❌ Error loading Google Maps API:', error);
            setLoadError(error as Error);
            setIsLoaded(false);
        }
    }, []);

    const value = {
        isLoaded,
        loadError,
        setMap,
        isMarkerLibraryLoaded,
    };

    return (
        <GoogleMapsContext.Provider value={value}>
            {children}
        </GoogleMapsContext.Provider>
    );
};
